import { formatDate } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, Inject,
    Input, LOCALE_ID,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { Coerce, KalDate, KalSelectionModel } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { BaseListComponent } from 'src/app/_core/tools/base-list.component';
import { ListingComponent } from 'src/app/_shared/components/listing/listing.component';
import {
    SharedBeneficiaryListFiltersComponent
} from 'src/app/_shared/components/shared-beneficiary-list-filters/shared-beneficiary-list-filters.component';
import {
    SharedBeneficiaryListDatasource
} from 'src/app/_shared/components/shared-beneficiary-list/shared-beneficiary-list.datasource';
import { KalTableComponent, KalTableHeader } from 'src/app/_shared/modules/kal-table/kal-table.module';
import { HomeSetSubNotificationTypeAction } from 'src/app/_shared/stores/home/home.actions';
import { BeneficiaryNotificationsTypes, HomeState } from 'src/app/_shared/stores/home/home.state';
import {
    BeneficiarySetBeneficiaryIdAction
} from 'src/app/board/beneficiary/stores/beneficiary-edit/beneficiary-edit.actions';
import { yesNo } from 'src/app/utils/boolean.utils';
import { applicationCodes } from 'src/environments/application-codes';
import { constants } from 'src/environments/constants';
import { environment } from 'src/environments/environment';
import {
    ArrayOfKeyValueModelDto,
    InfoColDataGridModelDto,
    MutationEnregistrerNouvelleVueParDefautArgs,
    TypesValeursEnum,
    VueDto
} from 'src/graphql/generated-types';

@Component({
    selector: 'app-shared-beneficiary-list',
    templateUrl: './shared-beneficiary-list.component.html',
    styleUrls: ['./shared-beneficiary-list.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedBeneficiaryListComponent extends BaseListComponent implements OnInit, AfterViewInit {

    private static listingId = 'SharedBeneficiaryListComponent';

    // Enum
    TypesValeursEnum = TypesValeursEnum;
    OrdreDeTri: string[] = [];
    dataSource: SharedBeneficiaryListDatasource;
    canEditBeneficary = false;

    @Input() selection = new KalSelectionModel<any>();
    @Coerce('boolean')
    @Input() isModalContext = false;
    @Input() hiddenBeneficiaryIdList: number[] = [];
    @ViewChild(KalTableComponent) kalTable: KalTableComponent<any>;
    @ViewChild(SharedBeneficiaryListFiltersComponent) beneficiaryFiltersComponent: SharedBeneficiaryListFiltersComponent;
    @ViewChild(ListingComponent) listingComponent: ListingComponent<any>;

    defaultPageSizeCode = applicationCodes.BNFNLI;

    constructor(private readonly graphqlService: GraphqlService,
                protected readonly cdr: ChangeDetectorRef,
                @Inject(LOCALE_ID) private readonly locale: string,
                private readonly layoutService: LayoutService) {
        super(cdr);
    }

    get columnsDto(): InfoColDataGridModelDto[] {
        return this.dataSource.columnsDto;
    }

    get BenefAvecDroitActif(): boolean {
        return this.dataSource.BenefAvecDroitActif;
    }

    get ListeVues(): VueDto[] {
        return this.dataSource.ListeVues;
    }

    get CurrentVue(): VueDto {
        return this.dataSource.CurrentVue;
    }

    get NombreBenefTot(): number {
        return this.dataSource.NombreBenefTot;
    }

    get NombreBenefTotFiltre(): number {
        return this.dataSource.NombreBenefTotFiltre;
    }

    get hasSelection(): boolean {
        return (!this.selection.multiple || !this.selection.all) ? !this.selection.isEmpty() : (this.NombreBenefTotFiltre - this.selection.removed.length > 0);
    }

    get columns(): KalTableHeader[] {
        return this.dataSource.columns;
    }

    get columnsForCSVExport(): KalTableHeader[] | null {
        if(this.isModalContext) {
            return null;
        }

        const normalizeExport = (row: KalTableHeader, code: (string | number | symbol)): string => {
            const column = this.getColumnByCode(code as string);

            switch (column.TypeValeur) {
                case TypesValeursEnum.Date:
                    // Utilisation du formatDate au lieu de l'exportDate car la date présente dans row[code] a un format invalide
                    return !!row[code] ? formatDate(this.toDate(row[code]), 'dd/MM/yyyy', this.locale) : '';
                case TypesValeursEnum.ListeValeurs:
                    return this.findValue(column.ListeValeurs, row[code]);
                default:
                    return row[code];
            }
        };

        return this.columns.map(column => ({...column, exporter: (row, code) => normalizeExport(row, code)}));
    }

    get displayedColumns(): string[] {
        return this.dataSource.columns?.map(c => c.code.toString()) || [];
    }

    get listingId(): string {
        return SharedBeneficiaryListComponent.listingId;
    }

    toDate(text: string): Date {
        return new KalDate(text, environment.date.format.human).toDate();
    }

    toYesNoEmpty(value: any): String {
        return !value ? '' : yesNo(value);
    }

    fetchNewVue(vue: VueDto): void {
        this.dataSource.CurrentVue = vue;
        this.watch$ = this.wrapLoader$(this.saveNewDefaultVue$()).pipe(
            tap(() => {
                this.dataSource.page.first();
                this.dataSource.refresh();
                this.kalTable.render({recalculateColumnWidths: true});
            })
        );
    }

    getColumnByCode(code: string): InfoColDataGridModelDto {
        return this.columnsDto?.find(c => c.CodeColonne === code) || {};
    }

    changeBenefAvecDroitActif(BenefAvecDroitActif: boolean): void {
        this.dataSource.BenefAvecDroitActif = BenefAvecDroitActif;
        // La réinitialisation de la pagniation va déclencher la récupération des données
        this.dataSource.page.first();
        this.dataSource.refresh();
    }

    findValue(list: ArrayOfKeyValueModelDto, id): string {
        return list?.KeyValueModelDTO?.find(k => k.Id + '' === id + '')?.Valeur;
    }

    getBenefDroitLabel(value = this.BenefAvecDroitActif): string {
        switch (value) {
            case true:
                return 'Bénéficiaire en droit actif';
            case false:
                return 'Bénéficiaire sans droits actifs';
            case null:
                return 'Bénéficiaires tous droits confondus';
        }
    }

    /**
     * Fonction utilisée seulement dans le cadre des modales de filtrage bénef.
     * Si on est en sélection multiple il est possible de sélectionner tous les bénéficiaires donc on fait une requête
     * qui va s'en charger.
     */
    getAll$(): Observable<{ [key: string]: string }[]> {
        return this.dataSource.getAll$();
    }

    navigateToBeneficiaryEdit(beneficiaryId: number = null): void {
        if (this.dataSource.loading.value) {
            return;
        }

        // Pour ajouter un bénéficiaire il faut mettre l'Id à 0
        this.watch$ = this.dispatch$(new BeneficiarySetBeneficiaryIdAction(0)).pipe(
            tap(() => this.layoutService.navigate({content: 'beneficiary/edit', focus: 'empty'}, {
                primaryRoute: 'content',
                queryParams: {
                    [constants.queryParamNames.beneficiaryIdQueryParamKey]: beneficiaryId,
                    [ListingComponent.keepFiltersParamName]: '1'
                }
            }))
        );
    }

    private createDataSource(): void {
        this.dataSource = new SharedBeneficiaryListDatasource({
                loading: false,
                filters: {
                    ...SharedBeneficiaryListFiltersComponent,
                    hiddenBeneficiaryIdList: this.hiddenBeneficiaryIdList
                },
                page: {
                    size: this.paginationSize,
                    number: 0
                }
            },
            this.graphqlService,
            this.store);
    }

    private updateNotificationsFilters$(): Observable<any> {
        const notificationType = this.snapshot(HomeState.subNotificationType);

        if (!notificationType) {
            return of(null);
        }

        const getValueFromCode: (code: BeneficiaryNotificationsTypes) => boolean = code => notificationType === code ? false : null;

        this.listingComponent.updateForm({
            Criteres: {
                IsCriteresSociauxValides: getValueFromCode(BeneficiaryNotificationsTypes.UNVALIDATED_SOCIAL_CRITERIA)
            },
            AyantsDroits: {
                IsConjointValide: getValueFromCode(BeneficiaryNotificationsTypes.UNVALIDATED_PARTNER),
                IsEnfantValide: getValueFromCode(BeneficiaryNotificationsTypes.UNVALIDATED_CHILDREN),
                IsExterneValide: getValueFromCode(BeneficiaryNotificationsTypes.UNVALIDATED_EXTERNS)
            }
        });
        this.listingComponent.applyFilters();

        // On Reset les notifications pour que lorsqu'on revient sur la page on recupere la liste seulement avec les filtres par défaut
        return this.dispatch$(new HomeSetSubNotificationTypeAction());
    }

    private saveNewDefaultVue$(): Observable<string> {
        return this.graphqlService.mutateOne<'EnregistrerNouvelleVueParDefaut', MutationEnregistrerNouvelleVueParDefautArgs>({
            mutation: gql`
                mutation EnregistrerNouvelleVueParDefaut($contexte: ContextInfosInput, $idNouvelleVue: Float) {
                    EnregistrerNouvelleVueParDefaut(contexte: $contexte, idNouvelleVue: $idNouvelleVue)
                }
            `,
            variables: {
                idNouvelleVue: this.CurrentVue.Id
            }
        });
    }

    ngOnInit(): void {
        this.createDataSource();

        this.watch$ = this.layoutService.checkUserAccess$('GestionBenefFicheSignaletique_ViewModel').pipe(
            filter((canShowBeneficariesDetails) => !!canShowBeneficariesDetails),
            switchMap(() => this.selection.changes),
            filter(selection => !!selection.added[0]?.ID && !this.isModalContext),
            map((selection) => +selection.added[0]?.ID),
            switchMap(id => this.layoutService.navigate({
                content: 'beneficiary/detail',
                focus: 'beneficiary/detail/activities'
            }, {
                primaryRoute: 'content',
                queryParams: {[constants.queryParamNames.beneficiaryIdQueryParamKey]: id}
            }))
        );

        this.watch$ = this.layoutService.checkUserAccess$('GestionDossierBenefNew_ViewModel').pipe(
            tap(canEditBeneficary => this.canEditBeneficary = canEditBeneficary)
        );
    }

    ngAfterViewInit(): void {
        this.updateNotificationsFilters$();

        this.watch$ = this.dataSource.dataLoading.pipe(
            tap(value => {
                this.loading = value;
            })
        );

        this.watch$ = this.listingComponent.filtersDisplayed$.pipe(
            tap(value => {
                this.loading = !value && this.dataSource.dataLoading.value;
            })
        );
    }
}
